// LoginForm.js

import React, { useState } from 'react';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput } from '../../utils/sanitize';
import { loginUser } from '../../services/AccountsServices';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTenant, saveTenant } from '../../utils/tenantUtils';
import axiosInstance from '../../services/axiosInstance';
import {
  DEFAULT_NAVIGATION_PREFERENCES,
  isPhoneViewport,
  resolveHomePath,
} from '../../utils/homePage';

const navigateAfterLogin = async (navigate, tenantSlug) => {
  sessionStorage.removeItem('lastPath');

  let prefs = DEFAULT_NAVIGATION_PREFERENCES;
  try {
    const profileRes = await axiosInstance.get('/user/profile/');
    prefs = profileRes.data.navigation_preferences || DEFAULT_NAVIGATION_PREFERENCES;
  } catch {
    // Fall back to defaults if profile is not available yet
  }

  const platform = isPhoneViewport() ? 'mobile' : 'desktop';
  const homePath = resolveHomePath(tenantSlug, prefs, platform);
  navigate(homePath, { replace: true });
};

const LoginForm = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();

  const handleInputChange = (e) => {
    // Don't sanitize password input to preserve spaces and special characters
    const value = e.target.name === 'password' ? e.target.value : sanitizeInput(e.target.value);
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

const [lastSubmitTime, setLastSubmitTime] = useState(Date.now());

const validateForm = (formData) => {
  const validationErrors = {};

  if (!formData.username?.trim()) {
    validationErrors.username = 'Username is required';
  }

  if (!formData.password?.trim()) {
    validationErrors.password = 'Password is required';
  } else if (formData.password.length < 5) {
    validationErrors.password = 'Password must be at least 5 characters';
  }

  return validationErrors;
};

const handleSubmit = async (e) => {
  e.preventDefault();

  // 1. Check rate limiting
  if (Date.now() - lastSubmitTime < 1000) {
    setErrorMessage('Please wait before trying again');
    return;
  }
  setLastSubmitTime(Date.now());

  // 2. Validate form inputs
  const validationErrors = validateForm(formData);
  if (Object.keys(validationErrors).length > 0) {
    setErrorMessage(Object.values(validationErrors).join(', '));
    return;
  }

  // 3. Proceed with login attempt
  try {
    const response = await loginUser(formData);
    // Check for both possible token formats
    if (response?.access_token || response?.access) {
      setIsAuthenticated(true);
      sessionStorage.removeItem('lastPath');

      // Use redirect_path from API if provided, otherwise fallback to tenant-aware path
      if (response?.redirect_path) {
        const m = response.redirect_path.match(/^\/tenant-access\/([^/]+)\/?/);
        if (m) {
          const slug = m[1];
          const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/i, '');
          let apiOrigin = '';
          try {
            if (apiBase) apiOrigin = new URL(apiBase).origin;
          } catch {
            apiOrigin = '';
          }
          const sameApiOrigin = apiOrigin && window.location.origin === apiOrigin;
          if (sameApiOrigin) {
            // Production-style: tenant_access refreshes cookies then SPA applies home preference.
            sessionStorage.setItem('justLoggedIn', '1');
            window.location.href = response.redirect_path;
          } else {
            // Split-origin dev: navigate directly to the user's configured home page.
            saveTenant(slug);
            await navigateAfterLogin(navigate, slug);
          }
        } else {
          sessionStorage.setItem('justLoggedIn', '1');
          window.location.href = response.redirect_path;
        }
      } else {
        const tenant = getTenant();
        await navigateAfterLogin(navigate, tenant);
      }
    } else {
      setErrorMessage('Login successful but authentication token not received');
    }
  } catch (error) {
    const message = handleError(error, 'LoginForm');
    setErrorMessage(message);
  }
};



  return (
    <Card className="login-form-card p-4 mx-auto mt-5" style={{ maxWidth: '400px' }}>
      {errorMessage && (
        <Alert variant="danger">{errorMessage}</Alert>
      )}
      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label>Username:</Form.Label>
          <Form.Control
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <br />
        <Form.Group>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <Form.Label>Password:</Form.Label>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
              style={{ 
                padding: '0', 
                fontSize: '0.875rem', 
                textDecoration: 'none',
                color: '#6c757d'
              }}
            >
              {showPassword ? '🙈 Hide' : '👁️ Show'}
            </Button>
          </div>
          <Form.Control
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            autoComplete="current-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            inputMode="text"
            style={{ fontFamily: 'monospace' }}
            required
          />
          <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
            Note: On mobile, ensure your password is entered exactly as created, including spaces.
          </Form.Text>
        </Form.Group>
        <br />
        <Button type="submit" variant="primary">
          Login
        </Button>
      </Form>
    </Card>
  );
};

export default LoginForm;
